// CustomerRequests
import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Select, MenuItem, Typography, Box, TableSortLabel, TablePagination, } from "@mui/material";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import ImageModel from "../components/ImageModel";

const CustomerRequests = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState("desc"); // Sorting order
    const [orderBy, setOrderBy] = useState("createdAt"); // Column to sort by
    const [page, setPage] = useState(0); // Current page
    const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
    const [open, setOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState("");
    const [describe, setDescribe] = useState("");

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "sign_board_requests"));
                const requests = querySnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .filter((request) => !request.hasOwnProperty("userId"));
                setData(requests);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    const handleStatusChange = async (id, newStatus) => {
        try {
          await updateDoc(doc(db, "sign_board_requests", id), { isVerified: newStatus });
          setData((prevData) =>
            prevData.map((item) => (item.id === id ? { ...item, isVerified: newStatus } : item))
          );
        } catch (error) {
          console.error("Error updating status:", error);
        }
      };

    const formatDate = (timestamp) => {
        const date = new Date(Number(timestamp));
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toISOString().split("T")[0];
    };

    const handleOpen = (imageURL) => {
        setCurrentImage(imageURL);
        setOpen(true);
    };
    const handleOpentext = (text) => {
        setDescribe(text);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setCurrentImage("");
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);

        const sortedData = [...data].sort((a, b) => {
            let aValue = a[property];
            let bValue = b[property];

            // Handle null or empty values
            if (aValue === null || aValue === "") aValue = "";
            if (bValue === null || bValue === "") bValue = "";

            if (property === "createdAt") {
                // Handle "Created At" as a date or timestamp
                aValue = new Date(aValue).getTime(); // Convert to timestamp
                bValue = new Date(bValue).getTime();

                if (isNaN(aValue)) aValue = 0; // Handle invalid dates
                if (isNaN(bValue)) bValue = 0;

                return isAsc ? aValue - bValue : bValue - aValue;
            }

            if (["fullName", "companyName", "address"].includes(property)) {
                // For these fields, perform a strict dictionary (case-insensitive) comparison
                return isAsc
                    ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
                    : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
            }

            // General string comparison for other fields
            if (typeof aValue === "string" && typeof bValue === "string") {
                return isAsc
                    ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
                    : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
            }

            return 0; // Default fallback (in case of unexpected types)
        });

        setData(sortedData);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortData = (array, comparator) => {
        const stabilizedArray = array.map((el, index) => [el, index]);
        stabilizedArray.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedArray.map((el) => el[0]);
    };

    const getComparator = (order, orderBy) => {
        return order === "desc"
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const sortedData = sortData(data, getComparator(order, orderBy));

    return (
        <Box>
            <Typography variant="h4" sx={{ p: 2 }}>
                Customer Requests
            </Typography>
            <ImageModel currentImage={currentImage} open={open} handleClose={handleClose} description={describe}/>
            <Box sx={{ padding: 2 }}>
                {loading ? (
                    <Typography>Loading...</Typography>
                ) : (
                    <>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: "black", // Black background for the header
                                        }}
                                    >
                                        {[
                                            { id: "createdAt", label: "Created At" },
                                            { id: "fullName", label: "Full Name" },
                                            { id: "phoneNumber", label: "Phone" },
                                            { id: "companyName", label: "Company Name" },
                                            { id: "address", label: "Address" },
                                            { id: "request", label: "Request" },
                                            { id: "boardWidth", label: "Width" },
                                            { id: "boardHeight", label: "Height" },
                                            { id: "description", label: "Matter" },
                                            { id: "fileURL", label: "File" },
                                            { id: "isVerified", label: "Status" },
                                        ].map((headCell) => (
                                            <TableCell
                                                key={headCell.id}
                                                sx={{
                                                    color: "white", // Ensure default text is white
                                                    fontWeight: "bold",
                                                    textAlign: "start",
                                                    "& .MuiTableSortLabel-root": {
                                                        color: "white", // Default text color
                                                    },
                                                    "& .MuiTableSortLabel-root.Mui-active": {
                                                        color: "red", // Change color to red for the active (sorted) header
                                                    },
                                                    "& .MuiTableSortLabel-root:hover": {
                                                        color: "red", // Text color on hover
                                                    },
                                                    "& .MuiTableSortLabel-icon": {
                                                        color: "white !important", // Ensure the sort icon is white
                                                    },
                                                    "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
                                                        color: "red !important", // Sort icon turns red for the active column
                                                    },
                                                }}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === headCell.id}
                                                    direction={orderBy === headCell.id ? order : "asc"}
                                                    onClick={() => handleRequestSort(headCell.id)}
                                                >
                                                    {headCell.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    "&:hover": {
                                                        backgroundColor: "#dcfcff",
                                                    },
                                                    backgroundColor: index % 2 === 0 ? "transparent" : "#f0f0f0", // Grey background for even rows
                                                }}
                                            >
                                                <TableCell>{formatDate(row.createdAt)}</TableCell>
                                                <TableCell>{row.fullName}</TableCell>
                                                <TableCell>{row.phoneNumber}</TableCell>
                                                <TableCell>{row.companyName}</TableCell>
                                                <TableCell>{row.address}</TableCell>
                                                <TableCell>{row.request}</TableCell>
                                                <TableCell>{row.boardWidth}</TableCell>
                                                <TableCell>{row.boardHeight}</TableCell>
                                                <TableCell>
                                                    {row.description ?
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => {
                                                                setDescribe(row.description);
                                                                handleOpentext(row.description);
                                                            }}
                                                        >
                                                            Read
                                                        </Button> : "No Description"}
                                                </TableCell>
                                                <TableCell>
                                                    {row.fileURL ? (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => handleOpen(row.fileURL)}
                                                        >
                                                            View
                                                        </Button>
                                                    ) : (
                                                        "No File"
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={row.isVerified}
                                                        onChange={(e) => handleStatusChange(row.id, e.target.value)}
                                                        size="small"
                                                    >
                                                        <MenuItem value="Not Verified">Not Verified</MenuItem>
                                                        <MenuItem value="Completed">Completed</MenuItem>
                                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                                        <MenuItem value="Rejected">Rejected</MenuItem>
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>

                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default CustomerRequests;